import { store } from '@store/index';
import { resetStep } from '@store/slices/login.slice';
import { clearPartnerSession } from '@store/slices/partnerSession.slice';
import type { PartnerSessionState } from '@store/slices/partnerSession.slice';
import { getAccessToken } from '@utils/helpers';
import { emitPartnerObservabilityEvent } from '@utils/partnerObservability';
import { getPartnerSessionErrorPath } from '@utils/partnerSessionError';
import { capturePartnerSsoUnauthorized } from '@utils/ssoObservability';
import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';

interface ApiErrorResponse {
  error?: {
    message?: string;
    errorMessage?: string;
  };
}

const headers = {
  'Content-Type': 'application/json',
};
const DEFAULT_API_TIMEOUT_MS = 50000;
const SSO_CODE_EXCHANGE_TIMEOUT_MS = 8000;

// Create an axios instance
// In Jest: process.env.VITE_CLIENT_SERVICE_URL is set in jest setup file
// In Vite: This will be replaced at build time by Vite's define config
const baseURL = process.env.VITE_CLIENT_SERVICE_URL || 'http://localhost:3000';
const ssoBaseURL = baseURL.replace(/\/pub\/?$/, '');

const axiosInstance = axios.create({
  baseURL,
  timeout: DEFAULT_API_TIMEOUT_MS,
  headers,
});

const ssoAxiosInstance = axios.create({
  baseURL: ssoBaseURL,
  timeout: SSO_CODE_EXCHANGE_TIMEOUT_MS,
  headers,
});

// Request Interception
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(undefined, (error: AxiosError) => {
  if (axios.isCancel(error)) return Promise.reject(error);
  const { response } = error || {};
  const status = response?.status;

  // Handle 401 Unauthorized
  if (status === 401) {
    const { partnerSession } = store.getState() as {
      partnerSession?: PartnerSessionState;
    };
    const activePartnerSession = partnerSession?.partnerSession;

    if (activePartnerSession) {
      capturePartnerSsoUnauthorized(activePartnerSession.partnerId);
      emitPartnerObservabilityEvent({
        event: 'session_failed',
        partnerSession: activePartnerSession,
        reason: 'unauthorized',
      });
      store.dispatch(clearPartnerSession());
      store.dispatch(resetStep());
      window.location.href = getPartnerSessionErrorPath('unauthorized');
      return Promise.reject(error);
    }

    store.dispatch(resetStep());
    window.location.href = '/';
    return Promise.reject(error);
  }

  // Show toast error message
  const errorData = response?.data as ApiErrorResponse;
  const errMsg =
    errorData?.error?.message ||
    errorData?.error?.errorMessage ||
    'Something went wrong. Please contact support.';
  toast.error(errMsg);

  return Promise.reject(error);
});

ssoAxiosInstance.interceptors.response.use(undefined, (error: AxiosError) => {
  return Promise.reject(error);
});

export { axiosInstance, ssoAxiosInstance };
